/**
 * Load / library / autosave hooks for the editor V2 rewrite.
 *
 * Ported from the `VideoEditor.tsx` monolith:
 *  - load effect (GET /api/editor-projects/:id → store.hydrate)
 *  - media library effect (GET /api/ai/wavespeed/history → store.setLibrary)
 *  - debounced autosave (POST /api/editor-projects on content change)
 *
 * No UI state here — draft-load/save messaging, blob-upload-before-save,
 * and render orchestration stay out of scope for this hook (task 3 of the
 * rewrite is hooks-only, wiring the store from task 2).
 */
'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useAuthToken } from '@/hooks/useAuthToken';
import { useEditorSession } from '@/hooks/useEditorSession';
import { useEditorStore, type EditorState } from '@/lib/editor/editorStore';
import { recalcClipStarts, type EditorMedia } from '@/lib/editor/editorClips';
import { extractVideoMetadata } from '@/lib/utils/video-metadata';
import type { EditorProject } from '@/types/editor';
import type { GenerationItem } from '@/components/studio/GenerationsGrid';

const AUTOSAVE_DEBOUNCE_MS = 1800;

type ContentSnapshot = Pick<
  EditorState,
  | 'clips'
  | 'layers'
  | 'assets'
  | 'audioTrack'
  | 'audioTracks'
  | 'crossfadeDuration'
  | 'filterId'
  | 'aspectRatio'
  | 'genProposals'
  | 'trackSettings'
  | 'modelId'
>;

function selectContent(state: EditorState): ContentSnapshot {
  return {
    clips: state.clips,
    layers: state.layers,
    assets: state.assets,
    audioTrack: state.audioTrack,
    audioTracks: state.audioTracks,
    crossfadeDuration: state.crossfadeDuration,
    filterId: state.filterId,
    aspectRatio: state.aspectRatio,
    genProposals: state.genProposals,
    trackSettings: state.trackSettings,
    modelId: state.modelId,
  };
}

function contentUnchanged(a: ContentSnapshot, b: ContentSnapshot): boolean {
  return (
    a.clips === b.clips &&
    a.layers === b.layers &&
    a.assets === b.assets &&
    a.audioTrack === b.audioTrack &&
    a.audioTracks === b.audioTracks &&
    a.crossfadeDuration === b.crossfadeDuration &&
    a.filterId === b.filterId &&
    a.aspectRatio === b.aspectRatio &&
    a.genProposals === b.genProposals &&
    a.modelId === b.modelId &&
    // `trackSettings` is a plain object rebuilt on every patch (see
    // `setTrackSettings`); compare by value rather than relying on reference
    // identity so this stays correct even if a future mutation path reuses
    // the same object reference.
    JSON.stringify(a.trackSettings) === JSON.stringify(b.trackSettings)
  );
}

export interface UseEditorProjectResult {
  draftLoaded: boolean;
  saving: boolean;
  /** Explicit immediate save, bypassing the debounce (reused by the render flow). */
  save: () => Promise<void>;
}

export function useEditorProject(projectId: string): UseEditorProjectResult {
  const { authedFetch } = useAuthToken();
  const { touch } = useEditorSession();

  const draftLoaded = useEditorStore((state) => state.draftLoaded);
  const saving = useEditorStore((state) => state.saving);

  const lastSavedRef = useRef('');
  const saveTimeoutRef = useRef<number | null>(null);
  // Reentrancy guard: `doSave` uploads local blobs then POSTs, so two
  // concurrent calls (a slow autosave still in flight when Render calls
  // `save()` directly, or a save slower than the debounce while edits
  // re-arm the timer) would otherwise both upload + presign + PUT the same
  // still-`blob:` clip, producing duplicate S3 objects/Video docs and a
  // racing `setState({ clips, library })`. Callers that arrive while a save
  // is in flight await the same promise instead of starting a new one.
  const inFlightSaveRef = useRef<Promise<void> | null>(null);

  // --- Load -----------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    authedFetch(`/api/editor-projects/${projectId}`)
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        if (json?.success && json.project) {
          useEditorStore.getState().hydrate(json.project as EditorProject);
          // Seed the dedupe baseline so autosave doesn't immediately re-POST
          // the project we just loaded.
          lastSavedRef.current = JSON.stringify(useEditorStore.getState().toSavePayload());
        }
      })
      .catch(() => {
        // Load failed (network/parse) — still unblock the editor below.
      })
      .finally(() => {
        // `hydrate` already sets draftLoaded on success; this covers the
        // failure path (and is a harmless no-op on the success path).
        if (!cancelled) {
          useEditorStore.setState({ draftLoaded: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authedFetch, projectId]);

  // --- Media library ----------------------------------------------------
  useEffect(() => {
    if (!draftLoaded) return;
    let cancelled = false;

    authedFetch('/api/ai/wavespeed/history?type=video&limit=12')
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        const videos: EditorMedia[] = Array.isArray(json?.data)
          ? (json.data as GenerationItem[])
              .filter((item) => item.status === 'completed' && item.outputs?.[0])
              .map((item) => ({
                id: item.jobId,
                videoId: (item as any).videoId ?? undefined,
                title: item.prompt || 'Untitled video',
                src: item.s3Url || item.outputs[0],
                source: 'studio' as const,
                createdAt: item.createdAt,
                duration: (item as any).durationSec ?? undefined,
                contentAnalysis: (item as any).contentAnalysis ?? undefined,
              }))
          : [];

        // Preserve any local (not-yet-uploaded) items already in the store's
        // library — same merge the monolith did — so this effect never
        // clobbers uploads added elsewhere.
        const prevLibrary = useEditorStore.getState().library;
        const uploads = prevLibrary.filter((item) => item.source === 'upload');
        const serverIds = new Set(videos.map((item) => item.id));
        useEditorStore.getState().setLibrary([...uploads.filter((item) => !serverIds.has(item.id)), ...videos]);
      })
      .catch(() => {
        // Best-effort library fetch; leave whatever is already in the store.
      });

    return () => {
      cancelled = true;
    };
  }, [authedFetch, draftLoaded]);

  // --- Blob → S3 upload (parity port from the monolith's saveDraft) -------
  // Local imports (MediaPanel) live as `blob:` object URLs backed by a `File`
  // stashed on the store's library item. Those URLs are process-local and die
  // on reload, so before ANY save/render we upload each local file to S3 and
  // rewrite the clip/library entries to point at the durable URL. Ported from
  // `VideoEditor.tsx` `uploadLocalVideosForServer` (~:1203). Throws on failure
  // so the caller can abort the save (a later change / explicit save retries).
  const uploadLocalClips = useCallback(async () => {
    const state = useEditorStore.getState();
    const blobClips = state.clips.filter((clip) => clip.src.startsWith('blob:'));
    if (blobClips.length === 0) return;

    const files = new Map<string, File>();
    for (const clip of blobClips) {
      const media = state.library.find((item) => item.id === clip.mediaId);
      if (media?.file) files.set(clip.mediaId, media.file);
    }
    if (files.size === 0) return;

    let nextClips = [...state.clips];
    let nextLibrary = [...state.library];

    for (const [mediaId, file] of files) {
      const metadata = await extractVideoMetadata(file);
      const presignRes = await authedFetch('/api/videos/upload/presign', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, fileType: file.type, fileSize: file.size }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok || !presign?.uploadUrl || !presign?.videoId) {
        throw new Error(presign?.error || 'Video upload presign failed');
      }

      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
          // Presign is signed with CacheControl; the same header must be sent
          // on PUT or S3 won't persist it.
          'Cache-Control': 'public, max-age=31536000, immutable',
        },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Video upload failed: HTTP ${putRes.status}`);

      const completeRes = await authedFetch('/api/videos/upload/complete', {
        method: 'POST',
        body: JSON.stringify({
          videoId: presign.videoId,
          title: file.name.replace(/\.[^/.]+$/, ''),
          description: 'Uploaded from Studio editor',
          metadata: { ...metadata, fileSize: file.size, format: file.type.split('/')[1] || 'mp4' },
        }),
      });
      const complete = await completeRes.json();
      const video = complete?.video;
      if (!completeRes.ok || !complete?.success || !video?.id || !video?.s3Url) {
        throw new Error(complete?.error || 'Video upload complete failed');
      }

      nextClips = nextClips.map((clip) =>
        clip.mediaId === mediaId
          ? {
              ...clip,
              mediaId: video.id,
              videoId: video.id,
              src: video.s3Url,
              duration: video.duration || clip.duration,
              source: 'upload' as const,
            }
          : clip
      );
      nextLibrary = nextLibrary.map((item) =>
        item.id === mediaId
          ? {
              ...item,
              id: video.id,
              videoId: video.id,
              src: video.s3Url,
              duration: video.duration || item.duration,
              file: undefined,
            }
          : item
      );
    }

    useEditorStore.setState({ clips: recalcClipStarts(nextClips), library: nextLibrary });
  }, [authedFetch]);

  // --- Save (explicit + debounced autosave) ------------------------------
  // `saving` (store state) is UI-only feedback, not a mutex — it doesn't stop
  // a second overlapping call. `doSave` itself is the mutex: if a save is
  // already in flight (e.g. a slow autosave still awaiting `uploadLocalClips`
  // / the POST when the user hits Render, which calls `save()` immediately),
  // new callers await the SAME in-flight promise instead of racing a second
  // `uploadLocalClips` over the same `blob:` clip (which would presign/PUT/
  // complete the same local file twice).
  const doSave = useCallback(async () => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current;

    const run = (async () => {
      // Persist local blobs to S3 first so the saved payload has durable URLs.
      // On failure, bail out of this save attempt (leaves a `blob:` clip that a
      // later autosave / explicit save will retry) rather than saving dead URLs.
      try {
        await uploadLocalClips();
      } catch {
        return;
      }

      const payload = useEditorStore.getState().toSavePayload();
      const snapshot = JSON.stringify(payload);
      if (snapshot === lastSavedRef.current) return;

      useEditorStore.getState().setSaving(true);
      try {
        const response = await authedFetch('/api/editor-projects', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const json = await response.json();
        if (response.ok && json?.success && json.project) {
          const saved = json.project as EditorProject;
          lastSavedRef.current = snapshot;
          useEditorStore.getState().setProjectId(saved.id);
          touch(saved.id);
        }
      } catch {
        // Best-effort autosave; a later content change (or explicit save)
        // will retry.
      } finally {
        useEditorStore.getState().setSaving(false);
      }
    })();

    inFlightSaveRef.current = run.finally(() => {
      inFlightSaveRef.current = null;
    });
    return inFlightSaveRef.current;
  }, [authedFetch, touch, uploadLocalClips]);

  useEffect(() => {
    const unsubscribe = useEditorStore.subscribe((state, prevState) => {
      if (!state.draftLoaded) return;
      if (contentUnchanged(selectContent(state), selectContent(prevState))) return;

      if (saveTimeoutRef.current !== null) window.clearTimeout(saveTimeoutRef.current);
      // Generation proposals are durable paid-task state. Persist them on the
      // next tick instead of leaving them inside the regular 1.8s editing
      // debounce: a reload/navigation during that window used to preserve the
      // assistant's “plan prepared” chat line but lose the actual approval
      // card and provider job lifecycle.
      const delay = state.genProposals !== prevState.genProposals ? 0 : AUTOSAVE_DEBOUNCE_MS;
      saveTimeoutRef.current = window.setTimeout(() => {
        saveTimeoutRef.current = null;
        void doSave();
      }, delay);
    });

    return () => {
      unsubscribe();
      if (saveTimeoutRef.current !== null) window.clearTimeout(saveTimeoutRef.current);
    };
  }, [doSave]);

  const save = useCallback(async () => {
    if (saveTimeoutRef.current !== null) {
      window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    await doSave();
  }, [doSave]);

  return { draftLoaded, saving, save };
}
